import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { pick, useSite } from "../useSite";

const fallbackNav = [
  { slug: "nav-journeys", title: "Journeys", link: "/journeys" },
  { slug: "nav-journal", title: "Journal", link: "/journal" },
  { slug: "nav-about", title: "About", link: "/about" },
  { slug: "nav-contact", title: "Contact", link: "/contact" },
];
const fallbackEnquire = { title: "Enquire now", link: "/enquire" };
const fallbackHeader = { title: "Vishmi Travels", subtitle: "Your traveling partner", body: "Sri Lanka", link: "/", image: "/images/vishmi-logo.png?v=dark" };
const fallbackFooterLogo = { title: "Vishmi", subtitle: "Travels", link: "/", image: "" };
const fallbackNote = {
  title: "Leaving traces of Ceylon hospitality in your heart.",
  subtitle: "info@vishmitravels.com",
  link: "mailto:info@vishmitravels.com",
  body: "All rights belong to Vishmi Travels (Pvt) Ltd, Sri Lanka. Reg. No. PV00258188.",
};
const fallbackPhones = [
  { slug: "footer-phone", title: "+94 76784 5585", link: "tel:+94767845585" },
];
const fallbackVisit = [
  { slug: "footer-link-packages", title: "Tour packages", link: "/journeys" },
  { slug: "footer-link-journal", title: "Journal", link: "/journal" },
  { slug: "footer-link-about", title: "About us", link: "/about" },
  { slug: "footer-link-plan", title: "Plan a journey", link: "/enquire" },
];
const fallbackSocial = [
  { slug: "instagram", title: "Instagram", link: "https://www.instagram.com/vishmitravels" },
  { slug: "facebook", title: "Facebook", link: "https://www.facebook.com/vishmitravels" },
  { slug: "tripadvisor", title: "Tripadvisor", link: "http://www.tripadvisor.com/vishmitravel" },
];
const fallbackWhatsapp = {
  slug: "whatsapp",
  title: "+94 76784 5585",
  link: "https://wa.me/94767845585",
  body: "Heyy, hope you are doing well?",
  subtitle: "Ask me anything..",
};

function toWhatsapp(record) {
  if (!record) return "";
  const fromTitle = String(record.title || "").replace(/\D/g, "");
  if (fromTitle.length >= 8) return `https://wa.me/${fromTitle}`;
  const link = String(record.link || "").trim();
  if (/wa\.me|whatsapp/i.test(link)) return link;
  const fromLink = link.replace(/\D/g, "");
  return fromLink.length >= 8 ? `https://wa.me/${fromLink}` : "";
}
const fallbackCompany = { title: "Vishmi Travels (Pvt) Ltd", subtitle: "Reg. No. PV00258188", body: "Sri Lanka" };

function FooterMark({ logo }) {
  if (!logo) return null;
  if (logo.image) {
    return (
      <Link to={logo.link || "/"} className="footer-mark">
        <img src={logo.image} alt={logo.title || "Vishmi Travels"} />
      </Link>
    );
  }
  const name = [logo.title, logo.subtitle].filter(Boolean).join(" ");
  if (!name) return null;
  return <Link to={logo.link || "/"} className="footer-mark">{name}</Link>;
}

function Logo({ logo, showLine }) {
  if (!logo) return null;
  if (logo.image) {
    return (
      <Link to={logo.link || "/"} className="brand">
        <img src={logo.image} alt={logo.title || "Vishmi Travels"} />
      </Link>
    );
  }
  return (
    <Link to={logo.link || "/"} className="brand">
      {logo.title ? <span>{logo.title}</span> : null}
      {logo.subtitle ? <span>{logo.subtitle}</span> : null}
      {showLine && logo.body ? <small>{logo.body}</small> : null}
    </Link>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [waNote, setWaNote] = useState(false);
  const [waThinking, setWaThinking] = useState(true);
  const [waHi, setWaHi] = useState(false);
  const [waDismissed, setWaDismissed] = useState(false);
  const [waIndex, setWaIndex] = useState(0);
  const { ofKind, block } = useSite();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const show = setTimeout(() => {
      setWaNote(true);
      if (reduce) setWaThinking(false);
    }, 3000);
    const hi = setTimeout(() => setWaHi(true), 8000);
    const think = reduce ? null : setTimeout(() => setWaThinking(false), 6200);
    return () => {
      clearTimeout(show);
      clearTimeout(hi);
      if (think) clearTimeout(think);
    };
  }, []);

  useEffect(() => {
    if (!waDismissed) return undefined;
    const timer = setTimeout(() => {
      setWaIndex((current) => current + 1);
      setWaThinking(false);
      setWaHi(true);
      setWaNote(true);
      setWaDismissed(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [waDismissed]);

  function dismissNote() {
    setWaNote(false);
    if (waIndex + 1 < messages.length) setWaDismissed(true);
  }

  const navItems = ofKind("nav");
  const menu = navItems === null ? fallbackNav : navItems.filter((item) => item.slug !== "nav-enquire");
  const enquire = navItems === null ? fallbackEnquire : navItems.find((item) => item.slug === "nav-enquire") || null;
  const footerItems = ofKind("footer");
  const phones = footerItems === null ? fallbackPhones : footerItems.filter((item) => String(item.link || "").startsWith("tel:"));
  const visit = footerItems === null ? fallbackVisit : footerItems.filter((item) => {
    const slug = item.slug || "";
    if (slug === "footer-note" || slug === "footer-company" || slug.startsWith("footer-label-")) return false;
    const link = String(item.link || "");
    return link && !link.startsWith("tel:") && !link.startsWith("mailto:");
  });
  const note = pick(block("footer-note"), fallbackNote);
  const company = pick(block("footer-company"), fallbackCompany);
  const visitHeading = pick(block("footer-label-visit"), { title: "Visit" })?.title || "Visit";
  const followHeading = pick(block("footer-label-follow"), { title: "Follow" })?.title || "Follow";
  const companyHeading = pick(block("footer-label-company"), { title: "Company" })?.title || "Company";
  const socials = (ofKind("social") || fallbackSocial).filter((item) => item.slug !== "whatsapp");
  const whatsapp = pick(block("whatsapp"), fallbackWhatsapp);
  const whatsappHref = toWhatsapp(whatsapp);
  const chatItems = ofKind("chat");
  const messages = chatItems === null
    ? [fallbackWhatsapp.body, fallbackWhatsapp.subtitle].filter(Boolean)
    : chatItems.map((item) => String(item.title || "").trim()).filter(Boolean);
  const whatsappText = messages[waIndex] || "";

  return (
    <>
      <header className="nav">
        <span onClick={() => setOpen(false)}>
          <Logo logo={pick(block("header-logo"), fallbackHeader)} showLine />
        </span>
        <button className="menu-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Close" : "Menu"}
        </button>
        <nav className={open ? "nav-links open" : "nav-links"} aria-label="Primary">
          {menu.map((item) => (
            <NavLink key={item.slug || item.link} to={item.link || "/"} onClick={() => setOpen(false)}>
              {item.title}
            </NavLink>
          ))}
          {enquire ? (
            <Link className="enquire-btn" to={enquire.link || "/enquire"} onClick={() => setOpen(false)}>
              {enquire.title}
            </Link>
          ) : null}
        </nav>
      </header>
      <main>{children}</main>
      <footer className="footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <FooterMark logo={pick(block("footer-logo"), fallbackFooterLogo)} />
            {note?.title ? <p className="footer-tagline">{note.title}</p> : null}
            <div className="footer-contact">
              {note?.subtitle ? <a href={note.link || `mailto:${note.subtitle}`}>{note.subtitle}</a> : null}
              {phones.map((phone) => {
                const digits = String(phone.title || phone.link || "").replace(/\D/g, "");
                const href = whatsappHref || (digits.length >= 8 ? `https://wa.me/${digits}` : phone.link);
                return (
                  <a key={phone.slug || phone.link} href={href} target="_blank" rel="noreferrer">{phone.title}</a>
                );
              })}
            </div>
          </div>
          <div className="footer-col">
            <p className="kicker">{visitHeading}</p>
            {visit.map((item) => (
              /^https?:/i.test(item.link || "") ? (
                <a key={item.slug || item.link} href={item.link} target="_blank" rel="noreferrer">{item.title}</a>
              ) : (
                <Link key={item.slug || item.link} to={item.link}>{item.title}</Link>
              )
            ))}
          </div>
          <div className="footer-col">
            <p className="kicker">{followHeading}</p>
            {socials.map((item) => (
              <a key={item.slug || item.link} href={item.link} target="_blank" rel="noreferrer">{item.title}</a>
            ))}
          </div>
          <div className="footer-col">
            <p className="kicker">{companyHeading}</p>
            {company?.title ? <p className="footer-company">{company.title}</p> : null}
            {company?.subtitle ? <p>{company.subtitle}</p> : null}
            {company?.body ? <p>{company.body}</p> : null}
          </div>
        </div>
        {note?.body ? <p className="footer-legal">{note.body}</p> : null}
      </footer>
      {whatsappHref ? (
        <div className="whatsapp-dock">
          {waNote && messages.length > 0 && (waIndex === 0 && waThinking || whatsappText) ? (
            <div className={`whatsapp-pop${waIndex === 0 && waThinking ? " is-thinking" : ""}${waHi ? " has-hi" : ""}`} role="status">
              {waHi ? <span className="whatsapp-pop-hi" aria-hidden="true">👋</span> : null}
              <button type="button" className="whatsapp-pop-close" aria-label="Close message" onClick={dismissNote}>×</button>
              {waIndex === 0 && waThinking ? (
                <span className="whatsapp-think" aria-label="Thinking">
                  <i /><i /><i />
                </span>
              ) : (
                <a href={whatsappHref} target="_blank" rel="noreferrer">{whatsappText}</a>
              )}
            </div>
          ) : null}
          <a className="whatsapp-fab" href={whatsappHref} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${whatsapp?.title || ""}`}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
            </svg>
          </a>
        </div>
      ) : null}
    </>
  );
}
